import { useNavigate } from 'react-router-dom';
import { useCompanies } from '../api/hooks';
import type { Company } from '../api/types';
import { Row, Surface } from '../components/ui';
import { useCompanyFilter } from '../state/company';
import { useUi } from '../state/ui';

/**
 * Přepínač firem. Na desktopu stálá součást levého sloupce pod názvem aplikace,
 * na mobilu chip v horní liště. Obojí ukazuje totéž a otevírá stejný seznam.
 */

const ALL = 'Všechny firmy';

const caret = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m7 10 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const check = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="m5 13 4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Druhý řádek: podle čeho firmu poznám, i když se jmenují podobně. */
function companySub(s: Company): string {
  const vat = s.vatPayer ? 'plátce DPH' : 'neplátce DPH';
  const base = s.ico ? `IČO ${s.ico} · ${vat}` : vat;
  return s.isTest ? `TESTOVACÍ · ${base}` : base;
}

/** Společný seznam pro obě podoby přepínače. */
function useCompanyPicker() {
  const { data: companies = [] } = useCompanies();
  const { companyId, setCompanyId } = useCompanyFilter();
  const { openSheet, closeSheet } = useUi();
  const navigate = useNavigate();

  const current = companies.find((s) => s.id === companyId);
  const name = current ? current.shortName || current.name : ALL;
  const sub = current
    ? companySub(current)
    : `${companies.length} ${companies.length === 1 ? 'firma' : companies.length < 5 ? 'firmy' : 'firem'}`;

  const open = () => {
    const pick = (id: string) => {
      setCompanyId(id);
      closeSheet();
    };
    openSheet(
      <>
        <h3>Firma</h3>
        <p className="muted mb">
          Firmy jsou oddělené: volba platí pro doklady, platby, kontakty i číselné řady.
        </p>
        <Surface>
          {companies.map((s) => (
            <Row key={s.id} chevron={false} onClick={() => pick(s.id)}>
              <div className="grow">
                <div className="l1">{s.name}</div>
                <div className="l2">{companySub(s)}</div>
              </div>
              {companyId === s.id && (
                <span className="ss-check" role="img" aria-label="zvoleno">
                  {check}
                </span>
              )}
            </Row>
          ))}
          <Row chevron={false} onClick={() => pick('all')}>
            <div className="grow">
              <div className="l1">{ALL}</div>
              <div className="l2">Jen ke čtení – vystavovat se dá pod konkrétní firmou</div>
            </div>
            {companyId === 'all' && (
              <span className="ss-check" role="img" aria-label="zvoleno">
                {check}
              </span>
            )}
          </Row>
          <Row
            chevron={false}
            onClick={() => {
              closeSheet();
              navigate('/firmy');
            }}
          >
            <div className="grow">
              <div className="l1">Výběr firmy…</div>
              <div className="l2">Celá obrazovka s přehledem firem</div>
            </div>
          </Row>
        </Surface>
      </>,
    );
  };

  return { name, sub, open };
}

/** Desktop: blok v levém sloupci, na první pohled je vidět zvolená firma. */
export function CompanySwitcher() {
  const { name, sub, open } = useCompanyPicker();
  return (
    <button
      type="button"
      className="company-switch"
      onClick={open}
      aria-haspopup="dialog"
      aria-label={`Firma: ${name}. Přepnout`}
    >
      <span className="grow">
        <span className="ss-name">{name}</span>
        <span className="ss-sub">{sub}</span>
      </span>
      {caret}
    </button>
  );
}

/** Mobil: chip v horní liště, otevírá stejný seznam. */
export function CompanyChip() {
  const { name, open } = useCompanyPicker();
  return (
    <button
      type="button"
      className="chip on"
      onClick={open}
      aria-haspopup="dialog"
      aria-label={`Firma: ${name}. Přepnout`}
    >
      {name} ▾
    </button>
  );
}
